import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { ILoginContext } from 'context';
import { ITimer, useLogin, useTimer } from 'hooks';

import styles from './Login2Fa.module.scss';

const RESEND_TIMER_DURATION = 60;

export function Login2Fa() {
  const { t } = useTranslation('auth');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const { loginModelData, on2FaLogin, on2faLoginResendCode }: ILoginContext = useLogin();
  const { seconds, startTimer }: ITimer = useTimer(RESEND_TIMER_DURATION);

  const [code, setCode] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const otpContainerRef = useRef<HTMLDivElement | null>(null);

  const isCodeValid: boolean = useMemo(() => code.length === 6 && /^\d+$/.test(code), [code]);

  const submitCode = (value: string): void => {
    if (!/^\d{6}$/.test(value) || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    on2FaLogin(value)
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
        setCode('');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleLogin = (): void => {
    if (!isCodeValid) {
      return;
    }

    submitCode(code);
  };

  const handleChange = (value: string): void => {
    setCode(value);
    if (/^\d{6}$/.test(value)) {
      submitCode(value);
    }
  };

  const handleResendCode = (): void => {
    on2faLoginResendCode()
      .then(() => {
        enqueueSnackbar(t('codeResent'), { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      });

    startTimer(RESEND_TIMER_DURATION);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const firstInput = otpContainerRef.current?.querySelector('input');
      firstInput?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div className={styles.form}>
      <Typography variant={isMobile ? 'h2' : 'h1'} component={isMobile ? 'h2' : 'h1'}>
        {t('login2faTitle')}
      </Typography>

      <Typography variant='body2'>{t('login2faDescription')}</Typography>

      <Typography variant='body2'>
        {t('verificationCodeSentEnding', { digits: loginModelData.phone!.slice(-4) })}
      </Typography>

      <div ref={otpContainerRef}>
        <MuiOtpInput
          autoFocus
          className={styles.input}
          value={code}
          onChange={handleChange}
          length={6}
          TextFieldsProps={{ InputProps: { componentsProps: { input: { pattern: '\\d*' } } } }}
        />
      </div>

      <Stack className={styles.buttonsContainer}>
        <Button variant='outlined' disabled={!!seconds} onClick={handleResendCode}>
          {t('resendCode')}
        </Button>
        <Button variant='contained' disabled={!isCodeValid || isSubmitting} onClick={handleLogin} type='submit'>
          {t('continue')}
        </Button>
      </Stack>

      {!!seconds && <Typography variant='body1'>{t('resendInSeconds', { seconds })}</Typography>}
    </div>
  );
}
